import { validateDischargePdfStep } from "./dischargePdfValidation";

export const getPastMaxDate = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().split('T')[0];
};

export const getTodayMaxDate = () => {
  return new Date().toISOString().split('T')[0];
};

export const isFutureOrToday = (dateStr) => {
  if (!dateStr) return false;
  const input = new Date(dateStr);
  input.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return input >= today;
};

export const isFutureDate = (dateStr) => {
  if (!dateStr) return false;
  const input = new Date(dateStr);
  input.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return input > today;
};

export const validateExperienceDates = ({ fromDate, toDate }) => {
  const errors = {};

  if (!fromDate) {
    errors.fromDate = 'From date is required';
  } else if (isFutureDate(fromDate)) {
    errors.fromDate = 'From date cannot be a future date';
  }

  if (!toDate) {
    errors.toDate = 'To date is required';
  } else if (isFutureDate(toDate)) {
    errors.toDate = 'To date cannot be a future date';
  } else if (fromDate && new Date(toDate) < new Date(fromDate)) {
    errors.toDate = 'To date cannot be earlier than From date';
  }

  return errors;
};

export const normalizePersonName = (name) =>
  (name || '').trim().replace(/\s+/g, ' ');

export const getSelectedMemberName = (familyMembers = [], memberId, memberName = '') => {
  if (memberName) {
    return memberName;
  }
  const member = familyMembers.find((item) => String(item.value) === String(memberId));
  return member?.text || '';
};

export const isNokMemberNameMatch = (nokName, memberName) => {
  const normalizedNokName = normalizePersonName(nokName);
  const normalizedMemberName = normalizePersonName(memberName);
  if (!normalizedNokName || !normalizedMemberName) {
    return false;
  }
  return normalizedNokName.toLowerCase() === normalizedMemberName.toLowerCase();
};

export const filterFamilyMembersByNokName = (familyMembers = [], nokName = '') => {
  const normalizedNokName = normalizePersonName(nokName);
  if (!normalizedNokName) {
    return [];
  }
  return familyMembers.filter((member) =>
    isNokMemberNameMatch(normalizedNokName, member.text)
  );
};

export const validateNokPppNameMatch = ({
  hasNok,
  nokName,
  memberId,
  familyMembers = [],
  memberName = '',
}) => {
  if (hasNok !== 'Yes') {
    return { isValid: true, message: '' };
  }
  if (!memberId) {
    return { isValid: true, message: '' };
  }

  const enteredNokName = normalizePersonName(nokName);
  const selectedMemberName = normalizePersonName(
    getSelectedMemberName(familyMembers, memberId, memberName)
  );

  if (!enteredNokName) {
    return {
      isValid: false,
      message: 'Please enter NOK name before PPP verification.',
    };
  }

  if (!selectedMemberName) {
    return {
      isValid: false,
      message: 'Please fetch family members and select the member matching the NOK name.',
    };
  }

  if (!isNokMemberNameMatch(enteredNokName, selectedMemberName)) {
    return {
      isValid: false,
      message: `Selected PPP member "${selectedMemberName}" does not match the entered NOK name "${enteredNokName}".`,
    };
  }

  return { isValid: true, message: '' };
};

export const buildOtpSentMessage = (response) => {
  const apiMessage = response?.data?.message;
  const otpDetailMessage = response?.data?.data?.message;
  const parts = [otpDetailMessage, apiMessage].filter(Boolean);
  return parts.length ? parts.join(' ') : 'OTP sent successfully';
};

export const validateStep = (step, formData) => {
  const errors = {};
  let isValid = true;

  switch (step) {
    case 0: // PPP Verification
      {
        if (!formData.pppVerified) {
          if (!formData.pppId) {
            errors.pppId = 'PPP ID is required';
            isValid = false;
          }
          if (!formData.memberId) {
            errors.memberId = 'Please select a family member';
            isValid = false;
          } else {
            errors.otp = 'Please complete OTP verification before proceeding';
            isValid = false;
          }
        }

        if (formData.hasNok === 'Yes') {
          if (!normalizePersonName(formData.nokName)) {
            errors.memberId = 'Please enter and save NOK name before PPP verification';
            isValid = false;
          } else if (formData.memberId) {
            const matchResult = validateNokPppNameMatch({
              hasNok: formData.hasNok,
              nokName: formData.nokName,
              memberId: formData.memberId,
              familyMembers: formData.familyMembers || [],
              memberName: formData.selectedMemberName,
            });
            if (!matchResult.isValid) {
              errors.memberId = matchResult.message;
              isValid = false;
            }
          }
        }
      }
      break;

    case 1: // Family Details
      if (!formData.fullName?.trim()) {
        errors.fullName = 'Full name is required';
        isValid = false;
      }
      if (!formData.fatherName?.trim()) {
        errors.fatherName = "Father's name is required";
        isValid = false;
      }
      if (!formData.motherName?.trim()) {
        errors.motherName = "Mother's name is required";
        isValid = false;
      }
      if (!formData.dob) {
        errors.dob = 'Date of birth is required';
        isValid = false;
      }
      if (!formData.gender) {
        errors.gender = 'Gender is required';
        isValid = false;
      }
      if (!formData.maritalStatus) {
        errors.maritalStatus = 'Marital status is required';
        isValid = false;
      }
      if (!formData.sasDistrict) {
        errors.sasDistrict = 'District Sainik and Ardh Sainik Welfare Office is required';
        isValid = false;
      }
     
      break;

    case 2: // Address Details
      if (!formData.state) {
        errors.state = 'State is required';
        isValid = false;
      }
      if (!formData.district) {
        errors.district = 'District is required';
        isValid = false;
      }
      if (!formData.tehsil) {
        errors.tehsil = 'Tehsil is required';
        isValid = false;
      }
      if (!formData.village?.trim()) {
        errors.village = 'Village is required';
        isValid = false;
      }
      if (!formData.address?.trim()) {
        errors.address = 'Address is required';
        isValid = false;
      }
      if (!formData.pinCode || !/^[1-9][0-9]{5}$/.test(formData.pinCode)) {
        errors.pinCode = 'Please enter a valid 6-digit PIN code';
        isValid = false;
      }
      break;

    case 3: // Contact Information
      if (!formData.email?.trim()) {
        errors.email = 'Email is required';
        isValid = false;
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        errors.email = 'Please enter a valid email address';
        isValid = false;
      }
      // if (!formData.mobile || !/^[6-9]\d{9}$/.test(formData.mobile)) {
      //   errors.mobile = 'Please enter a valid 10-digit mobile number';
      //   isValid = false;
      // }
      if (formData.altMobile && !/^[6-9]\d{9}$/.test(formData.altMobile)) {
        errors.altMobile = 'Please enter a valid 10-digit alternate mobile number';
        isValid = false;
      }
      break;

    case 4: // Other Details
     if (!formData.casteCategoryId) {
        errors.casteCategoryId = 'Caste category is required';
        isValid = false;
      }
      if (formData.isCasteCategoryOtherSelected && !formData.casteCategoryOther?.trim()) {
        errors.casteCategoryOther = 'Other caste category is required';
        isValid = false;
      }
      if (!formData.qualificationId) {
        errors.qualificationId = 'Qualification is required';
        isValid = false;
      }
      if (formData.isQualificationOtherSelected && !formData.qualificationOther?.trim()) {
        errors.qualificationOther = 'Other qualification is required';
        isValid = false;
      }
      if (!formData.engagementId) {
        errors.engagementId = 'Engagement type is required';
        isValid = false;
      }
      if (formData.isEngagementOtherSelected && !formData.engagementOther?.trim()) {
        errors.engagementOther = 'Other current engagement is required';
        isValid = false;
      }
      if (!formData.totalAnnualFamilyIncome) {
        errors.totalAnnualFamilyIncome = 'Annual family income is required';
        isValid = false;
      }
      if (formData.benchmarkDisability === '') {
        errors.benchmarkDisability = 'Please specify if you have benchmark disability';
        isValid = false;
      }
      if (formData.benchmarkDisability === 'Yes' && !formData.disabilityPercentage) {
        errors.disabilityPercentage = 'Disability percentage is required';
        isValid = false;
      }
      if (formData.incomeVerified === '') {
        errors.incomeVerified = 'Please specify if income is verified';
        isValid = false;
      }
     
      break;

    case 5: // Service Information
     if (formData.coveredUnderSocioEconomic === '') {
        errors.coveredUnderSocioEconomic = 'Please specify if you are covered under socio-economic criteria';
        isValid = false;
      }
      if (formData.collectLicenses) {
        if (formData.hasLicense === '') {
          errors.hasLicense = 'Please specify if you have a valid driving license';
          isValid = false;
        }
        if (formData.hasLicense === 'Yes') {
          const licenses = formData.licenses || [];
          if (
            licenses.length === 0 ||
            licenses.some((license) => !license?.licenseType || !license.licenseType.trim())
          ) {
            errors.licenseTypes = "Please enter at least one license type";
            isValid = false;
          }
        }
      }
      if (!formData.forceName) {
        errors.forceName = 'Force name is required';
        isValid = false;
      }
      if (!formData.forceNumber) {
        errors.forceNumber = 'Force/Service Number is required';
        isValid = false;
      }
      const dischargeValidation = validateDischargePdfStep({
        dischargePdf: formData.dischargePdf,
        savedDischargeDoc: formData.savedDischargeDoc,
        isReplacingDischargeDoc: formData.isReplacingDischargeDoc,
      });
      if (!dischargeValidation.valid) {
        errors.dischargePdf = dischargeValidation.message;
        isValid = false;
      }
      if (!formData.service) {
        errors.service = 'Service is required';
        isValid = false;
      }
      if (!formData.type) {
        errors.type = 'Type is required';
        isValid = false;
      }
      if (!formData.rank) {
        errors.rank = 'Rank is required';
        isValid = false;
      }
      if (!formData.medicalCategory) {
        errors.medicalCategory = 'Medical category is required';
        isValid = false;
      }
      if (!formData.enrollmentDate) {
        errors.enrollmentDate = 'Date of enrollment/joining is required';
        isValid = false;
      } else if (isFutureOrToday(formData.enrollmentDate)) {
        errors.enrollmentDate = 'Enrollment date cannot be today or a future date';
        isValid = false;
      }
      if (!formData.dischargeDate) {
        errors.dischargeDate = 'Discharge date is required';
        isValid = false;
      } else if (isFutureOrToday(formData.dischargeDate)) {
        errors.dischargeDate = 'Discharge date cannot be today or a future date';
        isValid = false;
      }
      if (!formData.dischargeBookNo?.trim()) {
        errors.dischargeBookNo = 'Discharge book number is required';
        isValid = false;
      }
      if (!formData.unitRetired?.trim()) {
        errors.unitRetired = 'Unit retired from is required';
        isValid = false;
      }
      // if (!formData.ppoNo?.trim()) {
      //   errors.ppoNo = 'PPO number is required';
      //   isValid = false;
      // }
      // if (!formData.ppoDate) {
      //   errors.ppoDate = 'PPO date is required';
      //   isValid = false;
      // }
      break;

    case 6: // Experience Details
      if (formData.hasExperience === 'Yes') {
        const govExperiences = formData.govExperiences || [];
        const privateExperiences = formData.privateExperiences || [];

        if (govExperiences.length === 0 && privateExperiences.length === 0) {
          errors.experienceForm = 'Please add at least one experience';
          isValid = false;
        }

        const allExperiences = [...govExperiences, ...privateExperiences];
        const hasInvalidDates = allExperiences.some((exp) => {
          const dateErrors = validateExperienceDates({
            fromDate: exp.fromDate,
            toDate: exp.toDate,
          });
          return Object.keys(dateErrors).length > 0;
        });
        if (hasInvalidDates) {
          errors.experienceForm =
            'Experience dates are invalid. Dates cannot be in the future, and To date cannot be before From date';
          isValid = false;
        }

        const missingPrivateDoc = privateExperiences.some(
          (exp) => !exp.document && !exp.existingDocId
        );
        if (missingPrivateDoc) {
          errors.experienceForm = 'Experience document is required for each private experience';
          isValid = false;
        }
      }
      break;

    case 7: // Undertaking & OTP
      if (!formData.otp || formData.otp.length !== 6) {
        errors.otp = 'Please enter the 6-digit OTP';
        isValid = false;
      }
      break;

    default:
      break;
  }

  return { isValid, errors };
};

export const getStepStatus = (step, formData, pppVerified) => {
  // If it's the first step, only check if the user is an ex-serviceman
  if (step === 0) {
    return formData.isExServiceman === 'Yes';
  }
  
  // For all other steps, check if the current step and all previous steps are valid
  for (let i = 0; i <= step; i++) {
    const { isValid } = validateStep(i, { ...formData, pppVerified });
    if (!isValid) return false;
  }
  
  return true;
};
